/**
 * Read Aloud TTS — text-to-speech voice generator
 *
 * Declares two WAR entries, both matched to every origin with no `use_dynamic_url`:
 * one for `offscreen/index.html`, and a broad group exposing every `*.js`, `*.css`
 * and `*.svg` plus the extension icons. So all of its bundled SVG logos and scripts
 * are fetchable from any page at a fixed URL.
 *
 * The vendor logo `app/icons/logo.svg` is the cleanest signal: an SVG loads through
 * `probeImage` with no CORS grant, so it survives a restrictive page CSP.
 *
 * Manifest verified against version 2.1.5.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'npdkkcjlmhcnnaoobfdjndibfkkhhdfn';

export const readAloud: ExtensionDefinition = {
  id: ID,
  name: 'Read Aloud TTS: Text to Speech AI Voice Generator',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 400_000,
  notes:
    'Exposes all .js / .css / .svg plus offscreen/index.html to every origin with no ' +
    'dynamic URL. The vendor logo SVG loads without a CORS grant; detection is conclusive.',
  signals: [
    {
      id: 'war-logo-svg',
      kind: 'web-accessible-resource',
      description:
        'Load app/icons/logo.svg via <img> — the vendor logo exposed to every origin; ' +
        'needs no CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'app/icons/logo.svg'),
    },
    {
      id: 'war-logo-vertical-svg',
      kind: 'web-accessible-resource',
      description:
        'Load app/logo_vertical.svg via <img> — a second vendor logo corroborating the ' +
        'identification.',
      weight: 0.5,
      run: () => probeImage(ID, 'app/logo_vertical.svg'),
    },
    {
      id: 'war-offscreen-html',
      kind: 'web-accessible-resource',
      description:
        'Fetch offscreen/index.html — an explicitly declared static entry point, ' +
        'independent of the SVG paths.',
      weight: 0.5,
      run: () => probeFetch(ID, 'offscreen/index.html'),
    },
  ],
};
